import { useTranslate } from 'react-admin';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { OFFICIAL_VAULTS_NS as NS } from './constants';
import type { OfficialVaultForm } from './useOfficialVaults';

export const ConfirmRequestDialog = ({
  form,
}: {
  form: Pick<OfficialVaultForm, 'open' | 'step' | 'summary' | 'submitting' | 'back' | 'submit'>;
}) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');

  return (
    <Dialog
      open={form.open && form.step === 'confirm'}
      onClose={form.submitting ? undefined : form.back}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      <DialogTitle>{translate(`${NS}.confirm.title`)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">{translate(`${NS}.confirm.description`)}</Alert>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate(`${NS}.confirm.field`)}</TableCell>
                  <TableCell>{translate(`${NS}.confirm.value`)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {form.summary.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell sx={{ color: 'text.secondary' }}>{translate(row.label)}</TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {row.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          {/* STORY-21 AC9 / D-30 clause 3: the fee and the lock sit on the confirm step too, not only on the form. */}
          <Alert severity="info">{translate(`${NS}.form.stakeNote`)}</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={form.back} disabled={form.submitting}>
          {translate(`${NS}.confirm.back`)}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={form.submit}
          disabled={form.submitting}
          loading={form.submitting}
        >
          {translate(`${NS}.confirm.submit`)}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
